import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getIdToken } from "firebase/auth";
import { getToken } from "firebase/app-check";
import { appCheck, auth, cloudFunctions, db } from "../config/firebase";

export interface RunSession {
  sessionId: string;
  eligible: boolean;
  firstAttempt: boolean;
  bountyGold: number;
}
export interface RunSettlement {
  rewardGold: number;
  creatorRewardGold: number;
  bountyGold: number;
  alreadySettled: boolean;
}

async function refreshCallableTokens(forceRefresh = false) {
  if (auth) {
    await auth.authStateReady();
    if (!auth.currentUser) throw new Error("Your sign-in session is not ready. Sign in again and retry.");
    await getIdToken(auth.currentUser, forceRefresh);
  }
  if (appCheck) await getToken(appCheck, forceRefresh);
}

async function call<T>(name: string, data: Record<string, unknown>): Promise<T> {
  if (!cloudFunctions) throw new Error("Challenge economy is unavailable.");
  await refreshCallableTokens();
  const callable = httpsCallable<Record<string, unknown>, T>(cloudFunctions, name);
  try {
    return (await callable(data)).data;
  } catch (error) {
    if ((error as { code?: string })?.code !== "functions/unauthenticated") throw error;
    await refreshCallableTokens(true);
    return (await callable(data)).data;
  }
}

export const beginMapRun = (mapId: string, revisionHash: string) =>
  call<RunSession>("beginMapRun", { mapId, revisionHash });
export const recordMapFailure = (sessionId: string) =>
  call<{ bountyGold: number }>("recordMapFailure", { sessionId });
export const settleMapRun = (sessionId: string, outcome: "complete" | "abandon") =>
  call<RunSettlement>("settleMapRun", { sessionId, outcome });
export const toggleMapSave = (mapId: string) =>
  call<{ saved: boolean; saveCount: number }>("toggleMapSave", { mapId });
export const recordHoardVisit = (ownerId: string) =>
  call<{ counted: boolean }>("recordHoardVisit", { ownerId });
export const spendGold = (amount: number) =>
  call<{ gold: number; spentGold: number }>("spendGold", { amount });
export const collectPhysicalGold = (amount: number) =>
  call<{ gold: number; collectedGold: number }>("collectPhysicalGold", { amount });
export const ensureWallet = () =>
  call<{ gold: number; created: boolean }>("ensureWallet", {});

export async function walletGold(userId: string) {
  if (!db) return null;
  const snapshot = await getDoc(doc(db, "rw_wallets", userId));
  return snapshot.exists() ? Math.max(0, Number(snapshot.data().gold) || 0) : null;
}

export async function isMapSaved(mapId: string, userId: string) {
  if (!db) return false;
  return (await getDoc(doc(db, "rw_map_saves", `${mapId}_${userId}`))).exists();
}
